import { memo, useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Select, Spin, Tooltip, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import CountUp from 'react-countup';
import dayjs from 'dayjs';
import { useListingCompanies } from '../../hooks/useCompanies';
import { usePendencyTrend } from '../../hooks/useTracker';
import './tracker.scss';

const PROCESS_COPY = {
    shoot: {
        title: 'Shoot',
        blurb: 'Work waiting to be photographed, then marked shoot-complete.',
    },
    assignment: {
        title: 'Assignment',
        blurb: 'New articles waiting to be assigned and DD approved.',
    },
    packing: {
        title: 'Packing',
        blurb: 'Shot items waiting to be packed into a box.',
    },
    outbound: {
        title: 'Outbound',
        blurb: 'Packed items waiting to ship.',
    },
};

const METRIC_HELP = {
    shoot: {
        incomingPendency:
            'Items already approved for shoot at the start of this range, but not yet shoot-complete. Support items are not counted.',
        workAdded: 'Items that were approved for shoot (DD approved) during this range.',
        workCompleted: 'Items whose shoot was marked complete during this range.',
        outgoingPendency:
            'Items still waiting for shoot at the end of this range: approved, but not yet shoot-complete. Support items are not counted.',
    },
    assignment: {
        incomingPendency:
            'Items that already existed at the start of this range and were not yet DD approved (or were approved after the start).',
        workAdded: 'New items created during this range. They enter the assignment queue.',
        workCompleted: 'Items that were DD approved during this range.',
        outgoingPendency:
            'Items still waiting for DD approval at the end of this range.',
    },
    packing: {
        incomingPendency:
            'Items ready to pack at the start of this range: shoot was already complete, or they were already marked as support, and they were not yet packed (or were packed after the start). Only items created after 1 Jan 2025 are counted.',
        workAdded:
            'Items that became ready to pack during this range: shoot completed, or they were marked as support.',
        workCompleted: 'Items that were packed into a box during this range.',
        outgoingPendency:
            'Items still waiting to be packed at the end of this range. Only items created after 1 Jan 2025 are counted.',
    },
    outbound: {
        incomingPendency:
            'Items already packed at the start of this range, but not yet shipped (or shipped after the start). Only items created after 1 Jan 2025 are counted.',
        workAdded: 'Items that were packed into a box during this range. They enter the outbound queue.',
        workCompleted: 'Items that were outbounded (shipped) during this range.',
        outgoingPendency:
            'Items still packed and waiting to ship at the end of this range. Only items created after 1 Jan 2025 are counted.',
    },
};

const GRAIN_COPY = {
    day: { chart: 'How the queue moved each day', table: 'Day', end: 'Waiting at end of day' },
    week: { chart: 'How the queue moved each week', table: 'Week', end: 'Waiting at end of week' },
    month: { chart: 'How the queue moved each month', table: 'Month', end: 'Waiting at end of month' },
    year: { chart: 'How the queue moved each year', table: 'Year', end: 'Waiting at end of year' },
};

function formatBucketLabel(bucket, grain, compact = false) {
    const from = dayjs(bucket.fromDate);
    const to = dayjs(bucket.toDate).subtract(1, 'minute');
    if (grain === 'week') {
        return compact ? from.format('DD MMM') : `${from.format('DD MMM')} – ${to.format('DD MMM')}`;
    }
    if (grain === 'month') return from.format(compact ? 'MMM' : 'MMM YYYY');
    if (grain === 'year') return from.format('YYYY');
    return from.format(compact ? 'DD' : 'DD MMM');
}

function describeProcess(title, summary) {
    if (!summary) return '';
    const { incomingPendency, outgoingPendency, workAdded, workCompleted, netChange } = summary;
    if (workAdded === 0 && workCompleted === 0) {
        return `${title} had no movement. ${outgoingPendency} items are still waiting.`;
    }
    if (netChange < 0) {
        return `${title} is catching up. Waiting dropped from ${incomingPendency} to ${outgoingPendency}. The team finished ${workCompleted} while ${workAdded} new items arrived.`;
    }
    if (netChange > 0) {
        return `${title} backlog grew from ${incomingPendency} to ${outgoingPendency}. ${workAdded} new items arrived and ${workCompleted} were finished.`;
    }
    return `${title} stayed even at ${outgoingPendency}. ${workAdded} arrived and ${workCompleted} were finished.`;
}

function statusFor(summary) {
    if (!summary) return { label: 'No data', className: 'tracker-pill-even' };
    if (summary.netChange < 0) return { label: 'Catching up', className: 'tracker-pill-down' };
    if (summary.netChange > 0) return { label: 'Backlog growing', className: 'tracker-pill-up' };
    return { label: 'Holding steady', className: 'tracker-pill-even' };
}

function Metric({ label, hint, help, value, className }) {
    return (
        <div className={`tracker-metric ${className}`}>
            <div className="tracker-metric-label">
                <span>{label}</span>
                {help ? (
                    <Tooltip title={help} overlayStyle={{ maxWidth: 320 }}>
                        <button type="button" className="tracker-metric-help" aria-label={`How ${label} is counted`}>
                            <QuestionCircleOutlined />
                        </button>
                    </Tooltip>
                ) : null}
            </div>
            <CountUp end={value || 0} separator="," duration={0.8} />
            <div className="tracker-metric-hint">{hint}</div>
        </div>
    );
}

function PaceBar({ added, completed }) {
    const total = added + completed;
    if (total === 0) {
        return <p className="tracker-pace-caption">No work arrived or finished in this period.</p>;
    }
    const finishedShare = Math.round((completed / total) * 100);
    const caption =
        completed > added
            ? 'The team is finishing faster than new work is arriving.'
            : added > completed
                ? 'New work is arriving faster than it is being finished.'
                : 'Work arriving and work finishing are in balance.';
    return (
        <div className="tracker-pace">
            <div className="tracker-pace-label">
                <span>Finished {completed}</span>
                <span>Arrived {added}</span>
            </div>
            <div className="tracker-pace-track" aria-hidden="true">
                <div className="tracker-pace-done" style={{ width: `${finishedShare}%` }} />
                <div className="tracker-pace-added" style={{ width: `${100 - finishedShare}%` }} />
            </div>
            <p className="tracker-pace-caption">{caption}</p>
        </div>
    );
}

function DailyTrendChart({ days, grain }) {
    const [hoverIndex, setHoverIndex] = useState(null);
    if (!days?.length) return null;
    const copy = GRAIN_COPY[grain] || GRAIN_COPY.day;
    const compactAxis = days.length > 14;
    const width = 560;
    const height = 210;
    const pad = { left: 36, right: 10, top: 16, bottom: 36 };
    const innerWidth = width - pad.left - pad.right;
    const innerHeight = height - pad.top - pad.bottom;
    const maxValue = Math.max(
        1,
        ...days.flatMap(day => [day.incomingPendency, day.workAdded, day.workCompleted, day.outgoingPendency])
    );
    const slot = innerWidth / days.length;
    const barWidth = Math.min(12, slot * 0.26);
    const y = value => pad.top + innerHeight - (value / maxValue) * innerHeight;
    const linePoints = days
        .map((day, index) => `${pad.left + slot * index + slot / 2},${y(day.outgoingPendency)}`)
        .join(' ');
    const hovered = hoverIndex != null ? days[hoverIndex] : null;
    const hoverX = hoverIndex != null ? ((pad.left + slot * hoverIndex + slot / 2) / width) * 100 : 0;
    const tooltipShift = hoverIndex === 0 ? '0' : hoverIndex === days.length - 1 ? '-100%' : '-50%';
    const labelEvery = days.length > 16 ? Math.ceil(days.length / 8) : 1;

    return (
        <div className="tracker-chart-wrap">
            <div className="tracker-chart-title">{copy.chart}</div>
            <div className="tracker-chart-canvas" onMouseLeave={() => setHoverIndex(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} className="tracker-chart" role="img" aria-label="Pendency trend">
                    <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + innerHeight} stroke="#e4e7ec" />
                    <line
                        x1={pad.left}
                        y1={pad.top + innerHeight}
                        x2={pad.left + innerWidth}
                        y2={pad.top + innerHeight}
                        stroke="#e4e7ec"
                    />
                    <text x={4} y={pad.top + 4} fontSize="10" fill="#98a2b3">
                        {maxValue}
                    </text>
                    <text x={4} y={pad.top + innerHeight} fontSize="10" fill="#98a2b3">
                        0
                    </text>
                    <polyline points={linePoints} fill="none" stroke="#c2410c" strokeWidth="2" />
                    {days.map((day, index) => {
                        const center = pad.left + slot * index + slot / 2;
                        const showLabel = index % labelEvery === 0 || index === days.length - 1;
                        return (
                            <g key={day.fromDate}>
                                <rect
                                    x={center - barWidth - 1}
                                    y={y(day.workAdded)}
                                    width={barWidth}
                                    height={Math.max(0, pad.top + innerHeight - y(day.workAdded))}
                                    fill="#2e90fa"
                                    rx="2"
                                />
                                <rect
                                    x={center + 1}
                                    y={y(day.workCompleted)}
                                    width={barWidth}
                                    height={Math.max(0, pad.top + innerHeight - y(day.workCompleted))}
                                    fill="#12b76a"
                                    rx="2"
                                />
                                <circle
                                    cx={center}
                                    cy={y(day.outgoingPendency)}
                                    r={hoverIndex === index ? 5 : 3}
                                    fill="#c2410c"
                                />
                                {showLabel ? (
                                    <text x={center} y={height - 12} textAnchor="middle" fontSize="10" fill="#667085">
                                        {formatBucketLabel(day, grain, compactAxis)}
                                    </text>
                                ) : null}
                            </g>
                        );
                    })}
                    {days.map((day, index) => (
                        <rect
                            key={`${day.fromDate}-hit`}
                            x={pad.left + slot * index}
                            y={pad.top}
                            width={slot}
                            height={innerHeight}
                            fill={hoverIndex === index ? 'rgba(16, 24, 40, 0.06)' : 'transparent'}
                            onMouseEnter={() => setHoverIndex(index)}
                        />
                    ))}
                </svg>
                {hovered ? (
                    <div className="tracker-chart-tooltip" style={{ left: `${hoverX}%`, transform: `translateX(${tooltipShift})` }}>
                        <strong>{formatBucketLabel(hovered, grain)}</strong>
                        <div className="tracker-tip-row">
                            <span>Waiting at start</span>
                            <b>{hovered.incomingPendency}</b>
                        </div>
                        <div className="tracker-tip-row">
                            <span>New work</span>
                            <b>{hovered.workAdded}</b>
                        </div>
                        <div className="tracker-tip-row">
                            <span>Finished</span>
                            <b>{hovered.workCompleted}</b>
                        </div>
                        <div className="tracker-tip-row">
                            <span>Waiting at end</span>
                            <b>{hovered.outgoingPendency}</b>
                        </div>
                    </div>
                ) : null}
            </div>
            <div className="tracker-legend">
                <span>
                    <span className="tracker-swatch" style={{ background: '#2e90fa' }} /> New work
                </span>
                <span>
                    <span className="tracker-swatch" style={{ background: '#12b76a' }} /> Finished
                </span>
                <span>
                    <span className="tracker-swatch" style={{ background: '#c2410c' }} /> {copy.end}
                </span>
            </div>
        </div>
    );
}

function ProcessPanel({ processKey, data, grain }) {
    const copy = PROCESS_COPY[processKey];
    const grainCopy = GRAIN_COPY[grain] || GRAIN_COPY.day;
    const summary = data?.summary;
    const status = statusFor(summary);
    const help = METRIC_HELP[processKey] || {};
    return (
        <section className="tracker-panel">
            <div className="tracker-panel-head">
                <div>
                    <h5>{copy.title}</h5>
                    <p>{describeProcess(copy.title, summary) || copy.blurb}</p>
                </div>
                <span className={`tracker-pill ${status.className}`}>{status.label}</span>
            </div>
            <div className="tracker-metrics">
                <Metric
                    label="Waiting at start"
                    hint="Incoming pendency"
                    help={help.incomingPendency}
                    value={summary?.incomingPendency}
                    className="tracker-metric-waiting"
                />
                <Metric
                    label="New work"
                    hint="Work added"
                    help={help.workAdded}
                    value={summary?.workAdded}
                    className="tracker-metric-added"
                />
                <Metric
                    label="Finished"
                    hint="Work completed"
                    help={help.workCompleted}
                    value={summary?.workCompleted}
                    className="tracker-metric-done"
                />
                <Metric
                    label="Waiting now"
                    hint="Outgoing pendency"
                    help={help.outgoingPendency}
                    value={summary?.outgoingPendency}
                    className="tracker-metric-now"
                />
            </div>
            <PaceBar added={summary?.workAdded || 0} completed={summary?.workCompleted || 0} />
            <DailyTrendChart days={data?.days || []} grain={grain} />
            <table className="tracker-table">
                <thead>
                    <tr>
                        <th>{grainCopy.table}</th>
                        <th>Start</th>
                        <th>Added</th>
                        <th>Finished</th>
                        <th>End</th>
                    </tr>
                </thead>
                <tbody>
                    {(data?.days || []).map(day => (
                        <tr key={day.fromDate}>
                            <td>{formatBucketLabel(day, grain)}</td>
                            <td>{day.incomingPendency}</td>
                            <td>{day.workAdded}</td>
                            <td>{day.workCompleted}</td>
                            <td>{day.outgoingPendency}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}

const Tracker = () => {
    const [companyCode, setCompanyCode] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
    const { data: companyList } = useListingCompanies();
    const trendMutation = usePendencyTrend();

    const trend = trendMutation.data?.data?.data;
    const selectedCompany = useMemo(
        () => (companyList || []).find(company => company.code === companyCode),
        [companyList, companyCode]
    );

    const headline = useMemo(() => {
        if (!trend) return '';
        const clientName = selectedCompany?.name || trend.companyCode;
        const shoot = describeProcess('Shoot', trend.shoot?.summary);
        const assignment = describeProcess('Assignment', trend.assignment?.summary);
        const packing = describeProcess('Packing', trend.packing?.summary);
        const outbound = describeProcess('Outbound', trend.outbound?.summary);
        return `${clientName}: ${shoot} ${assignment} ${packing} ${outbound}`;
    }, [trend, selectedCompany]);

    const onSubmit = () => {
        if (!companyCode) {
            message.warning('Please select a client');
            return;
        }
        if (!dateRange?.[0] || !dateRange?.[1]) {
            message.warning('Please select a date and time range');
            return;
        }
        const from = dateRange[0];
        const to = dateRange[1];
        if (!to.isAfter(from)) {
            message.warning('End time must be after start time');
            return;
        }
        trendMutation.mutate(
            {
                companyCode,
                fromDate: from.format('YYYY-MM-DDTHH:mm:ssZ'),
                toDate: to.format('YYYY-MM-DDTHH:mm:ssZ'),
            },
            {
                onError: err => {
                    const payload = err?.response?.data?.message;
                    message.error(typeof payload === 'string' ? payload : 'Could not load tracker numbers');
                },
            }
        );
    };

    return (
        <div className="tracker-page">
            <div className="tracker-header">
                <h4>Work tracker</h4>
                <p>See how waiting work, new work, and finished work moved for a client.</p>
            </div>
            <form
                className="tracker-filters"
                onSubmit={event => {
                    event.preventDefault();
                    onSubmit();
                }}
            >
                <div className="tracker-filter">
                    <label htmlFor="tracker-client">Client</label>
                    <Select
                        id="tracker-client"
                        showSearch
                        allowClear
                        size="large"
                        placeholder="Select a client"
                        value={companyCode}
                        onChange={setCompanyCode}
                        optionFilterProp="label"
                        options={(companyList || []).map(company => ({
                            value: company.code,
                            label: `${company.name} (${company.code})`,
                        }))}
                    />
                </div>
                <div className="tracker-filter" style={{ minWidth: 340 }}>
                    <label htmlFor="tracker-range">Date and time range</label>
                    <DatePicker.RangePicker
                        id="tracker-range"
                        size="large"
                        showTime={{ format: 'HH:mm' }}
                        format="DD/MM/YYYY HH:mm"
                        value={dateRange}
                        onChange={setDateRange}
                        style={{ width: '100%' }}
                    />
                </div>
                <Button className="tracker-filter-submit" type="primary" size="large" htmlType="submit" loading={trendMutation.isLoading}>
                    Submit
                </Button>
            </form>
            {headline ? <div className="tracker-story">{headline}</div> : null}
            <Spin spinning={trendMutation.isLoading}>
                <div className="tracker-body">
                    {!trend ? (
                        <div className="tracker-empty">
                            <Empty description="Pick a client and a date range, then click Submit." />
                        </div>
                    ) : (
                        <div className="tracker-grid">
                            <ProcessPanel
                                key={`shoot-${trend.companyCode}-${trend.fromDate}`}
                                processKey="shoot"
                                data={trend.shoot}
                                grain={trend.grain}
                            />
                            <ProcessPanel
                                key={`assignment-${trend.companyCode}-${trend.fromDate}`}
                                processKey="assignment"
                                data={trend.assignment}
                                grain={trend.grain}
                            />
                            <ProcessPanel
                                key={`packing-${trend.companyCode}-${trend.fromDate}`}
                                processKey="packing"
                                data={trend.packing}
                                grain={trend.grain}
                            />
                            <ProcessPanel
                                key={`outbound-${trend.companyCode}-${trend.fromDate}`}
                                processKey="outbound"
                                data={trend.outbound}
                                grain={trend.grain}
                            />
                        </div>
                    )}
                </div>
            </Spin>
        </div>
    );
};

export default memo(Tracker);
